import { formatMx } from '../utils/datetime';

/**
 * Las correcciones humanas de esta llamada, en un solo lugar.
 *
 * Por qué existe
 * =============
 * La boleta acumuló CUATRO mecanismos de corrección con semánticas distintas
 * —aprovechamiento, tipificación, penalización por error y veredictos de
 * criterio— y cada uno vive junto al dato que corrige, repartidos por una
 * pantalla de más de 4,000 px. Cada ubicación se justifica sola; el conjunto no.
 * Un supervisor no puede responder "¿qué se le tocó a esta llamada y quién?" sin
 * recorrerla entera.
 *
 * Lo pidió Samuel el 31-ago-2026, después de reportar que los 14 controles de
 * corrección de criterio eran enlaces de 11 px al 60% de la página — la misma
 * falla que ya había reportado con el botón del falso positivo.
 *
 * Qué hace y qué NO hace
 * ======================
 * **No duplica los controles: concentra el REGISTRO.** Donde el contexto importa
 * —el veredicto de un criterio se entiende viendo su cita y su peso— el control
 * se queda en su renglón. Dos caminos de escritura para el mismo dato es
 * exactamente lo que garantiza que un día difieran.
 *
 * Lo que aporta es lo que no existía: qué se corrigió, por quién, cuándo y con
 * qué motivo, junto y en orden. Y cuando no hay nada, lo dice — "sin
 * correcciones" es información, y un panel vacío sin texto se lee como un panel
 * roto.
 *
 * El agente también lo ve. Son correcciones sobre SU llamada.
 */

const GRIS = '#6b7280';
const AMBAR = '#fbbf24';
const VERDE = '#34d399';

function Renglon({ que, estado, autor, cuando, motivo, corregido }) {
  return (
    <tr>
      <td style={{ padding: '7px 10px', fontSize: 12.5, borderBottom: '1px solid #1c1c22', width: 190 }}>
        {que}
      </td>
      <td style={{ padding: '7px 10px', fontSize: 12.5, borderBottom: '1px solid #1c1c22' }}>
        <span style={{ color: corregido ? AMBAR : GRIS, fontWeight: corregido ? 600 : 400 }}>
          {estado}
        </span>
        {motivo && (
          <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', marginTop: 2 }}>
            «{motivo}»
          </div>
        )}
      </td>
      <td style={{
        padding: '7px 10px', fontSize: 11, color: GRIS,
        borderBottom: '1px solid #1c1c22', whiteSpace: 'nowrap',
      }}>
        {corregido
          ? <>{autor || 'sin autor registrado'}{cuando ? ` · ${formatMx(cuando)}` : ''}</>
          : '—'}
      </td>
    </tr>
  );
}

export default function PanelDeCorrecciones({ analysis, typification, boleta, replica }) {
  const filas = [];

  // 1. Aprovechamiento (PATCH /aprovechable, migración 011).
  const apro = analysis?.aprovechable_override;
  filas.push({
    que: 'Aprovechamiento',
    corregido: apro !== null && apro !== undefined,
    estado: apro === true ? 'Marcada como aprovechable a mano'
      : apro === false ? 'Marcada como NO aprovechable a mano'
      : 'Automático',
    autor: analysis?.aprovechable_override_by,
    cuando: analysis?.aprovechable_override_at,
  });

  // 2. Tipificación (POST /typification-override, migración 013).
  const tip = typification?.override;
  filas.push({
    que: 'Tipificación',
    corregido: Boolean(tip),
    estado: tip ? `Corregida a "${tip.manual_code}"` : 'Automática',
    autor: tip?.por,
    cuando: tip?.created_at,
    motivo: tip?.note,
  });

  // 3. Penalización por error (PATCH /boleta/error, migración 025). Sólo se
  //    puede DESCARTAR, nunca poner: por eso no entra al mecanismo general.
  const pen = (boleta?.criterios || []).find((c) => c.penalizacion);
  filas.push({
    que: 'Penalización por error',
    corregido: Boolean(pen?.descartado),
    estado: pen?.descartado
      ? 'Descartada — no resta puntos'
      : pen?.cumple === 'si' ? 'Vigente (−1.0)' : 'No hubo error',
    autor: pen?.descartado_por,
    cuando: pen?.descartado_at,
    motivo: pen?.descartado_nota,
  });

  // 4. Veredictos de criterio (PATCH /boleta/criterio, migración 028). Uno por
  //    criterio corregido: son los únicos que mueven la calificación además del
  //    descarte, así que cada uno va con su nombre.
  const corregidos = (boleta?.criterios || []).filter((c) => c.correccion);
  if (corregidos.length === 0) {
    filas.push({
      que: 'Criterios de la boleta',
      corregido: false,
      estado: 'Ninguno corregido',
    });
  } else {
    corregidos.forEach((c) => filas.push({
      que: c.etiqueta,
      corregido: true,
      estado: `El análisis dijo "${c.cumple}" · corregido a "${c.correccion.veredicto}"`,
      autor: c.correccion.por,
      cuando: c.correccion.at,
      motivo: c.correccion.motivo,
    }));
  }

  // 5. La resolución de la réplica. No es una corrección —no mueve la nota—
  //    pero es la decisión que suele explicar a las demás, así que se lee aquí.
  if (replica) {
    filas.push({
      que: 'Réplica del agente',
      corregido: Boolean(replica.resolucion),
      estado: replica.resolucion === 'aceptada' ? 'Se le dio razón'
        : replica.resolucion === 'rechazada' ? 'Se sostuvo la calificación'
        : 'Pendiente de revisar',
      autor: replica.resuelta_por,
      cuando: replica.resuelta_at,
    });
  }

  const cuantas = filas.filter((f) => f.corregido).length;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span>Correcciones de esta llamada</span>
        <span style={{
          fontSize: 11, textTransform: 'none', letterSpacing: 0,
          color: cuantas ? AMBAR : GRIS,
        }}>
          {cuantas === 0
            ? 'ninguna — todo viene del análisis automático'
            : `${cuantas} ${cuantas === 1 ? 'corrección humana' : 'correcciones humanas'}`}
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Qué', 'Estado', 'Quién y cuándo'].map((h) => (
                <th key={h} style={{
                  textAlign: 'left', padding: '6px 10px', fontSize: 10.5, color: GRIS,
                  textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600,
                  borderBottom: '1px solid #26262e',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => <Renglon key={`${f.que}-${i}`} {...f} />)}
          </tbody>
        </table>
      </div>

      {/* Los controles NO están aquí, y eso es deliberado: viven junto al dato
          que corrigen, donde se ve el contexto para decidir. Decirlo evita que
          alguien los busque en este panel y concluya que no existen — que es la
          confusión que este panel vino a quitar, no a mover. */}
      <div style={{ marginTop: 10, fontSize: 11, color: GRIS, lineHeight: 1.6 }}>
        Cada corrección se hace donde vive el dato: el aprovechamiento y la
        tipificación en el encabezado, la penalización y los criterios en la
        boleta de arriba. Aquí se lee lo que ya se decidió, junto y con nombre.
      </div>
    </div>
  );
}
